// "Bill to": type to find a saved client, or add a new one without leaving
// the invoice. A combobox: arrow keys move, Enter picks, Escape closes.
import { type FormEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type Client, clientInitials, contactLine, displayName, isEmailAddress } from '@/core/client';
import { nowStamp } from '@/core/dates';
import { newId } from '@/core/ids';
import { type ClientSnapshot, initialsOf } from '@/core/invoice';
import { track } from '@/data/analytics';
import { saveClient } from '@/data/store';
import { useAppData } from '@/app/AppData';
import { t } from '@/l10n/strings';
import { Avatar, Button, TextField } from '@/ui/controls';
import { Dialog } from '@/ui/Dialog';
import { Icon } from '@/ui/Icon';

interface Props {
  uid: string;
  clients: Client[];
  client: ClientSnapshot;
  clientId: string | null;
  error: string | null;
  onPick: (client: Client) => void;
}

export function ClientPicker({ uid, clients, client, error, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState<string | null>(null);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return clients.filter((c) => !q || [c.name, c.company, c.email, c.phone].some((s) => s.toLowerCase().includes(q))).slice(0, 8);
  }, [clients, query]);
  // The last option adds a new client.
  const count = matches.length + 1;

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const choose = (index: number) => {
    if (index < matches.length) {
      onPick(matches[index]);
      setOpen(false);
      setQuery('');
    } else {
      setOpen(false);
      setAdding(query.trim());
    }
  };

  const hasClient = client.name.trim() !== '';
  return (
    <div className="field picker" ref={box}>
      <label className="field__label" htmlFor={`${id}-input`}>
        {t('editorBillTo')}
      </label>
      <div className="input input--affix" aria-invalid={error ? true : undefined}>
        {hasClient && !open && <Avatar initials={initialsOf(client.name)} seed={client.name} />}
        <input
          ref={input}
          id={`${id}-input`}
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${id}-${active}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          autoComplete="off"
          value={open ? query : client.name}
          placeholder={hasClient ? client.name : t('clientPickerSearchClients')}
          onFocus={() => {
            setOpen(true);
            setActive(0);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setOpen(true);
              setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : -1) + count) % count);
            } else if (e.key === 'Enter' && open) {
              e.preventDefault();
              choose(active);
            } else if (e.key === 'Escape') {
              setOpen(false);
              setQuery('');
            }
          }}
        />
        <Icon name="down" size={18} className="muted" />
      </div>
      {open && (
        <ul className="picker__list" id={`${id}-list`} role="listbox" aria-label={t('clientPickerChooseASavedClient')}>
          {matches.map((c, i) => (
            <li key={c.id} id={`${id}-${i}`} role="option" aria-selected={i === active} className="palette__item" onMouseMove={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(i)}>
              <Avatar initials={clientInitials(c)} seed={displayName(c)} />
              <span className="grow">
                <b>{displayName(c)}</b>
                {contactLine(c) && <span className="caption">{contactLine(c)}</span>}
              </span>
            </li>
          ))}
          {matches.length === 0 && query.trim() && (
            <li className="caption" style={{ padding: '10px 12px' }} role="presentation">
              {t('clientPickerNoClientMatches', { query: query.trim() })}
            </li>
          )}
          <li id={`${id}-${matches.length}`} role="option" aria-selected={active === matches.length} className="palette__item" onMouseMove={() => setActive(matches.length)} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(matches.length)}>
            <span className="tile">
              <Icon name="userplus" size={18} />
            </span>
            <b className="brand-text">{t('clientPickerAddANewClient')}</b>
          </li>
        </ul>
      )}
      {error && (
        <span className="error" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
      {adding !== null && (
        <NewClientDialog
          initialName={adding}
          onClose={() => setAdding(null)}
          onSaved={(c) => {
            saveClient(uid, c);
            track('client_added', { source: 'picker', from_contacts: false });
            onPick(c);
            setAdding(null);
          }}
        />
      )}
    </div>
  );
}

function NewClientDialog({ initialName, onClose, onSaved }: { initialName: string; onClose: () => void; onSaved: (client: Client) => void }) {
  const { business } = useAppData();
  const [name, setName] = useState(initialName);
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const nameError = !name.trim() && !company.trim() ? t('errorNameOrCompanyMissing') : null;
  const emailError = email.trim() && !isEmailAddress(email) ? t('errorEmailInvalid') : null;
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    setShowErrors(true);
    if (nameError || emailError) return;
    const now = nowStamp();
    onSaved({ id: newId(), name: name.trim(), company: company.trim(), email: email.trim(), phone: phone.trim(), address: '', notes: '', businessId: business?.id ?? null, createdAt: now, updatedAt: now });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('clientPickerAddClient')}
      onSubmit={submit}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('commonCancel')}
          </Button>
          <Button type="submit" variant="action">
            {t('clientFormSaveClient')}
          </Button>
        </>
      }
    >
      <TextField label={t('clientPickerName')} value={name} onChange={(e) => setName(e.target.value)} error={showErrors ? nameError : null} autoFocus />
      <TextField label={t('clientPickerCompanyOptional')} value={company} onChange={(e) => setCompany(e.target.value)} />
      <div className="grid-2">
        <TextField label={t('clientPickerEmailOptional')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={showErrors ? emailError : null} />
        <TextField label={t('clientPickerPhoneOptional')} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
    </Dialog>
  );
}
